import React, { useContext, useState } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import { IconButton } from "@material-ui/core";
import { MoreVert, Replay } from "@material-ui/icons";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import TicketOptionsMenu from "../TicketOptionsMenu";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
    actionButtons: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        flexWrap: "nowrap", // Força a ficar na mesma linha sem descer
        gap: "4px",        // Espaçamento enxuto entre os botões
        marginLeft: "auto",
        paddingRight: "4px",
        "& button": {
            whiteSpace: "nowrap", // Impede o texto dos botões de quebrar linha
            padding: "4px 8px",   // Deixa os botões um pouco mais compactos
            fontSize: "0.75rem",  // Tamanho ideal para caber na barra superior
        },
    },
}));

const TicketActionButtons = ({ ticket }) => {
    const classes = useStyles();
    const history = useHistory();
    const [anchorEl, setAnchorEl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingClone, setLoadingClone] = useState(false);
    const ticketOptionsMenuOpen = Boolean(anchorEl);
    const { user } = useContext(AuthContext);

    const handleOpenTicketOptionsMenu = e => {
        setAnchorEl(e.currentTarget);
    };

    const handleCloseTicketOptionsMenu = e => {
        setAnchorEl(null);
    };

    const handleUpdateTicketStatus = async (e, status, userId) => {
        setLoading(true);
        try {
            await api.put(`/tickets/${ticket.id}`, {
                status: status,
                userId: userId || null,
            });

            setLoading(false);
            if (status === "open") {
                history.push(`/tickets/${ticket.id}`);
            } else {
                history.push("/tickets");
            }
        } catch (err) {
            setLoading(false);
            toastError(err);
        }
    };

    const handleCreateFromThis = async () => {
        setLoadingClone(true);
        try {
            const { data } = await api.post(`/tickets/${ticket.id}/clone`);
            setLoadingClone(false);
            
            // Redireciona a tela imediatamente para o novo ticket criado
            history.push(`/tickets/${data.id}`);
        } catch (err) {
            setLoadingClone(false);
            toastError(err);
        }
    };

    return (
        <div className={classes.actionButtons}>
            {ticket.status === "closed" && (
                <ButtonWithSpinner
                    loading={loading}
                    startIcon={<Replay />}
                    size="small"
                    onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
                >
                    {i18n.t("messagesList.header.buttons.reopen")}
                </ButtonWithSpinner>
            )}
            {ticket.status === "open" && (
                <>
                    <ButtonWithSpinner
                        loading={loadingClone}
                        startIcon={<AddCircleOutlineIcon />}
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={handleCreateFromThis}
                    >
                        Criar a partir deste
                    </ButtonWithSpinner>
                    <ButtonWithSpinner
                        loading={loading}
                        startIcon={<Replay />}
                        size="small"
                        onClick={e => handleUpdateTicketStatus(e, "pending", null)}
                    >
                        {i18n.t("messagesList.header.buttons.return")}
                    </ButtonWithSpinner>
                    <ButtonWithSpinner
                        loading={loading}
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={e => handleUpdateTicketStatus(e, "closed", user?.id)}
                    >
                        {i18n.t("messagesList.header.buttons.resolve")}
                    </ButtonWithSpinner>
                    <IconButton 
                        size="small" 
                        onClick={handleOpenTicketOptionsMenu}
                    >
                        <MoreVert />
                    </IconButton>
                    <TicketOptionsMenu
                        ticket={ticket}
                        anchorEl={anchorEl}
                        menuOpen={ticketOptionsMenuOpen}
                        handleClose={handleCloseTicketOptionsMenu}
                    />
                </>
            )}
            {ticket.status === "pending" && (
                <ButtonWithSpinner
                    loading={loading}
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
                >
                    {i18n.t("messagesList.header.buttons.accept")}
                </ButtonWithSpinner>
            )}
        </div>
    );
};

export default TicketActionButtons;